import React from "react";
import { useTranslation } from "react-i18next";

interface UnifiedLoadingProps {
  message?: string;
  size?: "sm" | "md" | "lg" | "xl";
  className?: string;
  showBackdrop?: boolean;
}

/**
 * UnifiedLoading Component
 * 
 * A standardized loading component that:
 * - Blocks page content with semi-transparent overlay
 * - Never blocks navigation elements (sidebar, navbar, mobile header, bottom bar)
 * - Uses z-index: 90 to stay below all navigation elements (z-index: 100+)
 * - Always allows navigation to remain accessible
 * 
 * Z-Index Scale:
 * - Loading Layer: 90 (below navigation)
 * - Navigation Layer: 100-199
 * - Modal Layer: 300-399
 */
const UnifiedLoading: React.FC<UnifiedLoadingProps> = ({
  message,
  size = "md",
  className = "",
  showBackdrop = true,
}) => {
  const { t } = useTranslation();
  const defaultMessage = message || t("Common.Pages.Loading");

  // Size classes for the spinner
  const sizeClasses = {
    sm: "tw-w-6 tw-h-6",
    md: "tw-w-8 tw-h-8",
    lg: "tw-w-12 tw-h-12",
    xl: "tw-w-16 tw-h-16",
  };

  const spinnerSizeClass = sizeClasses[size];

  return (
    <div
      className={`tw-fixed tw-inset-0 tw-flex tw-items-center tw-justify-center tw-pointer-events-none ${
        showBackdrop
          ? "tw-bg-black/40 tw-backdrop-blur-sm"
          : "tw-bg-transparent"
      } ${className}`}
      style={{
        zIndex: 90, // Always below navigation (100+) but above content
      }}
      role="status"
      aria-busy="true"
      aria-label={defaultMessage}
    >
      <div className="tw-flex tw-flex-col tw-items-center tw-gap-4 tw-rounded-xl tw-bg-white tw-p-8 tw-shadow-2xl dark:tw-bg-background-dark tw-pointer-events-auto">
        <div
          className={`tw-animate-spin tw-rounded-full tw-border-4 tw-border-solid tw-border-primary tw-border-t-transparent ${spinnerSizeClass}`}
        >
          <span className="tw-sr-only">{defaultMessage}</span>
        </div>
        <span className="tw-text-base tw-font-medium tw-text-gray-800 dark:tw-text-gray-200">
          {message || `${defaultMessage}...`}
        </span>
      </div>
    </div>
  );
};

export default UnifiedLoading;